import type { ElitePrimePricingDetailsResponse } from '../../types/elitePrimeSummary.types'
import type { EliteOnboardingStatusResponse } from '../../types/misc.types'
import { UNITS } from '../../types/misc.types'
import type {
  MarketHoldingsBreakdown,
  PricingDetailsContent,
  PricingDetailsRow,
  PricingSlabTable
} from './types/pricing.types'
import { convertUnitAndValue, roundOffAndFormatNumberWithCurrency } from '../../utils/utils'
import type { ElitePrimeFundAdditionalCharge, ElitePrimePortfolioSlab } from '../../types/elitePrimeSummary.types'
import { resolveElitePrimeUpgradeDetails } from './elitePlansUpgradeDetails'
import { shouldShowElitePlansMarketPricing } from './elitePlansPlusBannerDisplay'
import { ELITE_PRIME_FEE_SUFFIX } from './constants/ctaCopy'

const ONE_LAKH = 100_000
const ONE_CRORE = 10_000_000

function portfolioFeeLabel(percentage: number): string {
  const formatted = convertUnitAndValue({ value: percentage, unit: UNITS.PERCENTAGE })
  return `${formatted} of your portfolio`
}

/** Compact slab labels: ₹10L / ₹1.5cr (avoids convertNumberToCurrency rounding 10L–1Cr to ₹0 Cr). */
function formatCompactPortfolioAmount(amount: number): string {
  if (amount >= ONE_CRORE) {
    const crores = amount / ONE_CRORE
    if (Number.isInteger(crores)) {
      return `₹${crores}cr`
    }
    return `₹${parseFloat(crores.toFixed(2))}cr`
  }
  if (amount >= ONE_LAKH) {
    const lakhs = amount / ONE_LAKH
    if (Number.isInteger(lakhs)) {
      return `₹${lakhs}L`
    }
    return `₹${parseFloat(lakhs.toFixed(2))}L`
  }
  return String(roundOffAndFormatNumberWithCurrency(amount))
}

function formatPortfolioSlabLabel(minAmount: number, maxAmount: number): string {
  if (maxAmount <= 0) {
    return `Above ${formatCompactPortfolioAmount(minAmount)}`
  }
  if (minAmount <= 0) {
    return `Upto ${formatCompactPortfolioAmount(maxAmount)}`
  }
  return `${formatCompactPortfolioAmount(minAmount)} - ${formatCompactPortfolioAmount(maxAmount)}`
}

function formatSlabPrimeRate(slabPercentage: number, maxAmount: number): string {
  // Upto ₹5L slabs show no prime rate
  if (maxAmount > 0 && maxAmount <= 500_000) {
    return `-`
  }
  if (!slabPercentage) {
    return '0%'
  }
  // 3 decimals when the thousandths digit is non-zero; otherwise 2 (e.g. 0.125% vs 0.12%)
  const threeDecimals = slabPercentage.toFixed(3)
  if (threeDecimals.endsWith('0')) {
    return `${slabPercentage.toFixed(2)}%`
  }
  return `${threeDecimals}%`
}

function findActiveSlabIndex(
  slabs: ElitePrimePortfolioSlab[],
  effectiveSlabPricePercentage: number,
  portfolioValue: number
): number {
  if (effectiveSlabPricePercentage > 0) {
    const byPercentage = slabs.findIndex((slab) => {
      const slabPercentage = slab.slabPercentage?.amount ?? 0
      return slabPercentage === effectiveSlabPricePercentage
    })
    if (byPercentage >= 0) {
      return byPercentage
    }
  }

  if (portfolioValue <= 0) {
    return -1
  }

  return slabs.findIndex((slab) => {
    const minAmount = slab.minPortfolioValue?.amount ?? 0
    const maxAmount = slab.maxPortfolioValue?.amount ?? 0
    if (maxAmount <= 0) {
      return portfolioValue >= minAmount
    }
    return portfolioValue >= minAmount && portfolioValue < maxAmount
  })
}

function buildSlabTable(
  templateTable: PricingSlabTable | undefined,
  slabs: ElitePrimePortfolioSlab[],
  effectiveSlabPricePercentage: number,
  portfolioValue: number,
  roundedPricingValue: string
): PricingSlabTable {
  const activeSlabIndex = findActiveSlabIndex(slabs, effectiveSlabPricePercentage, portfolioValue)

  return {
    portfolioValueLabel: templateTable?.portfolioValueLabel ?? 'Portfolio Value',
    primeRateLabel: templateTable?.primeRateLabel ?? 'Prime Rate',
    maxFeeLabel: templateTable?.maxFeeLabel ?? 'Max Fee',
    roundedPricingLabel: templateTable?.roundedPricingLabel ?? 'Pricing (Rounded off)',
    roundedPricingValue,
    tiers: slabs.map((slab, index) => {
      const minAmount = slab.minPortfolioValue?.amount ?? 0
      const maxAmount = slab.maxPortfolioValue?.amount ?? 0
      const maxSlabPrice = slab.maxSlabPrice?.amount ?? 0
      const slabPercentage = slab.slabPercentage?.amount ?? 0

      return {
        id: `slab-${index}`,
        portfolioValue: formatPortfolioSlabLabel(minAmount, maxAmount),
        primeRate: formatSlabPrimeRate(slabPercentage, maxAmount),
        maxFee: `${roundOffAndFormatNumberWithCurrency(maxSlabPrice)}`,
        isActive: index === activeSlabIndex
      }
    })
  }
}

function buildMarketBreakdown(
  template: MarketHoldingsBreakdown | undefined,
  funds: ElitePrimeFundAdditionalCharge[],
  status: EliteOnboardingStatusResponse | undefined
): MarketHoldingsBreakdown | undefined {
  if (!template && funds.length === 0) {
    return undefined
  }

  const totalCharges = funds.reduce((sum, fund) => sum + (fund.additionalCharges?.amount ?? 0), 0)
  const portfolioPercentage = status?.marketPricingDetails?.portfolioPercentage?.amount
  const totalLabel =
    portfolioPercentage != null
      ? `Total (${convertUnitAndValue({ value: portfolioPercentage, unit: UNITS.PERCENTAGE })} of portfolio)`
      : (template?.totalLabel ?? 'Total')

  return {
    note: template?.note ?? '',
    holdingsLabel: template?.holdingsLabel ?? 'Your portfolio holdings',
    chargesLabel: template?.chargesLabel ?? 'Additional charges',
    rows: funds.map((fund, index) => ({
      id: `fund-charge-${index}`,
      logoSrc: fund.fundLogo ?? '',
      fundName: fund.fundName ?? '',
      portfolioValue: `${roundOffAndFormatNumberWithCurrency(fund.currentPortfolioValue?.amount ?? 0)}`,
      charge: `${roundOffAndFormatNumberWithCurrency(fund.additionalCharges?.amount ?? 0)}`,
      chargePercent: convertUnitAndValue({
        value: fund.additionalPercentage?.amount ?? 0,
        unit: UNITS.PERCENTAGE
      })
    })),
    totalLabel,
    totalValue: funds.length ? `${roundOffAndFormatNumberWithCurrency(totalCharges)}/yr` : undefined
  }
}

function resolveElitePrimePlanFees(status: EliteOnboardingStatusResponse | undefined) {
  const plan = status?.eligiblePlans?.find((entry) => entry.recommended) ?? status?.eligiblePlans?.[0]
  return {
    effectivePlanFees: plan?.effectivePlanFees?.amount ?? 0,
    effectiveSlabPricePercentage: plan?.effectiveSlabPricePercentage?.amount ?? 0
  }
}

function formatFeeWithSuffix(amount: number): string {
  return `${roundOffAndFormatNumberWithCurrency(amount)}${ELITE_PRIME_FEE_SUFFIX}`
}

function buildPricingRows(
  templateRows: PricingDetailsRow[],
  pricingDetails: ElitePrimePricingDetailsResponse,
  status: EliteOnboardingStatusResponse | undefined
): PricingDetailsRow[] {
  const portfolioValue = pricingDetails.currentPortfolioValue?.amount ?? 0
  const slabs = pricingDetails.portfolioSlabList ?? []
  const { effectivePlanFees, effectiveSlabPricePercentage } = resolveElitePrimePlanFees(status)
  const upgradeDetails = resolveElitePrimeUpgradeDetails(status)
  const upgradePricing = upgradeDetails?.upgradePricingDetails
  const appliedSlabRate = convertUnitAndValue({
    value: effectiveSlabPricePercentage,
    unit: UNITS.PERCENTAGE
  })

  return templateRows
    .filter((row) => !row.upgradeOnly || Boolean(upgradePricing))
    .map((row) => {
      if (row.id === 'portfolio-value') {
        return {
          ...row,
          value: `${roundOffAndFormatNumberWithCurrency(portfolioValue)}`
        }
      }

      if (row.id === 'slab-rate') {
        const roundedFee = upgradePricing ? upgradePricing.totalPayableAmount : effectivePlanFees
        return {
          ...row,
          value: appliedSlabRate,
          slabTable: buildSlabTable(
            row.slabTable,
            slabs,
            effectiveSlabPricePercentage,
            portfolioValue,
            formatFeeWithSuffix(roundedFee)
          )
        }
      }

      if (row.id === 'elite-prime-fee') {
        return {
          ...row,
          value: `${roundOffAndFormatNumberWithCurrency(effectivePlanFees)}`
        }
      }

      if (row.id === 'adjusted-max-slab' && upgradePricing) {
        return {
          ...row,
          value: formatFeeWithSuffix(upgradePricing.upgradePlanPrice)
        }
      }

      if (row.id === 'adjusted-remaining' && upgradePricing) {
        return {
          ...row,
          value: `- ${roundOffAndFormatNumberWithCurrency(upgradePricing.adjustmentFromCurrentPlan)}`
        }
      }

      if (row.id === 'pricing-rounded') {
        if (upgradePricing) {
          return {
            ...row,
            value: formatFeeWithSuffix(upgradePricing.totalPayableAmount),
            valueMuted: true
          }
        }
        return {
          ...row,
          value: formatFeeWithSuffix(effectivePlanFees)
        }
      }

      return row
    })
}

export function resolveElitePlansPricingDetailsDisplay({
  template,
  pricingDetails,
  status
}: {
  template: PricingDetailsContent
  pricingDetails?: ElitePrimePricingDetailsResponse
  status?: EliteOnboardingStatusResponse
}): PricingDetailsContent {
  const { effectivePlanFees, effectiveSlabPricePercentage } = resolveElitePrimePlanFees(status)
  const upgradeDetails = resolveElitePrimeUpgradeDetails(status)
  const upgradePricing = upgradeDetails?.upgradePricingDetails
  const displayFee = upgradePricing ? upgradePricing.totalPayableAmount : effectivePlanFees
  const marketPrice = status?.marketPricingDetails?.marketPrice?.amount
  const marketPortfolioPercentage = status?.marketPricingDetails?.portfolioPercentage?.amount
  const showMarketPricing = shouldShowElitePlansMarketPricing(status)

  const visibleTemplateRows = (template.rows ?? []).filter((row) => !row.upgradeOnly || Boolean(upgradePricing))

  const baseContent: PricingDetailsContent = {
    ...template,
    price: displayFee ? `${roundOffAndFormatNumberWithCurrency(displayFee)}` : undefined,
    assuranceBadge: effectiveSlabPricePercentage
      ? `${portfolioFeeLabel(effectiveSlabPricePercentage)} • Lowest price assurance`
      : undefined,
    marketEyebrow: showMarketPricing ? template.marketEyebrow : undefined,
    marketPrice:
      showMarketPricing && marketPrice != null ? `${roundOffAndFormatNumberWithCurrency(marketPrice)}` : undefined,
    marketPriceSuffix: showMarketPricing ? template.marketPriceSuffix : undefined,
    marketBadge:
      showMarketPricing && marketPortfolioPercentage != null
        ? `${portfolioFeeLabel(marketPortfolioPercentage)} via regular funds`
        : undefined,
    rows: visibleTemplateRows.map((row) => {
      if (!row.slabTable) {
        return { ...row, value: undefined }
      }
      return {
        ...row,
        value: undefined,
        slabTable: {
          ...row.slabTable,
          tiers: []
        }
      }
    }),
    marketBreakdown:
      showMarketPricing && template.marketBreakdown
        ? {
            ...template.marketBreakdown,
            rows: [],
            totalValue: undefined
          }
        : undefined
  }

  if (!pricingDetails) {
    return baseContent
  }

  return {
    ...baseContent,
    rows: buildPricingRows(template.rows ?? [], pricingDetails, status),
    marketBreakdown: showMarketPricing
      ? buildMarketBreakdown(template.marketBreakdown, pricingDetails.fundAdditionalChargesDetails ?? [], status)
      : undefined
  }
}
